import { NotificationData, notifications } from "@mantine/notifications";
import { ToastOptions } from "react-hot-toast";
import {
    ERROR_NOTIFICATION_CONFIG,
    LOADING_NOTIFICATION_CONFIG,
    SUCCESS_NOTIFICATION_CONFIG,
    WARNING_NOTIFICATION_CONFIG,
} from "./notifications.constants";
import { PromiseData } from "./notifications.types";

/**
 * Transforms the old configuration to the new one
 * @param old The old configuration
 * @returns The new configuration
 */
const transformOldConfiguration = (old?: ToastOptions): Partial<NotificationData> => {
    return {
        ...(old?.duration ? { autoClose: old?.duration } : {}),
    };
};

/**
 * Shows a notification
 * @param configuration Notification configuration or message
 * @param oldConfiguration Old notification configuration
 * @returns Notification ID
 */
const show = (configuration: NotificationData | string, oldConfiguration?: ToastOptions): string => {
    if (typeof configuration === "string") {
        return notifications.show({
            message: configuration,
            ...transformOldConfiguration(oldConfiguration),
        });
    }
    return notifications.show({
        ...configuration,
        ...transformOldConfiguration(oldConfiguration),
    });
};

/**
 * Shows a success notification
 * @param configuration Notification configuration or message
 * @param oldConfiguration Old notification configuration
 * @returns Notification ID
 */
const success = (configuration: NotificationData | string, oldConfiguration?: ToastOptions): string => {
    if (typeof configuration === "string") {
        return notifications.show({
            ...SUCCESS_NOTIFICATION_CONFIG,
            message: configuration,
            ...transformOldConfiguration(oldConfiguration),
        });
    }
    return notifications.show({
        ...SUCCESS_NOTIFICATION_CONFIG,
        ...configuration,
        ...transformOldConfiguration(oldConfiguration),
    });
};

/**
 * Shows an error notification
 * @param configuration Notification configuration or message
 * @param oldConfiguration Old notification configuration
 * @returns Notification ID
 */
const error = (configuration: NotificationData | string, oldConfiguration?: ToastOptions): string => {
    if (typeof configuration === "string") {
        return notifications.show({
            ...ERROR_NOTIFICATION_CONFIG,
            message: configuration,
            ...transformOldConfiguration(oldConfiguration),
        });
    }
    return notifications.show({
        ...ERROR_NOTIFICATION_CONFIG,
        ...configuration,
        ...transformOldConfiguration(oldConfiguration),
    });
};

/**
 * Shows a warning notification
 * @param configuration Notification configuration or message
 * @param oldConfiguration Old notification configuration
 * @returns Notification ID
 */
const warning = (configuration: NotificationData | string, oldConfiguration?: ToastOptions): string => {
    if (typeof configuration === "string") {
        return notifications.show({
            ...WARNING_NOTIFICATION_CONFIG,
            message: configuration,
            ...transformOldConfiguration(oldConfiguration),
        });
    }
    return notifications.show({
        ...WARNING_NOTIFICATION_CONFIG,
        ...configuration,
        ...transformOldConfiguration(oldConfiguration),
    });
};

/**
 * Shows a loading notification
 * @param configuration Notification configuration or message
 * @param oldConfiguration Old notification configuration
 * @returns Notification ID
 */
const loading = (configuration: NotificationData | string, oldConfiguration?: ToastOptions): string => {
    if (typeof configuration === "string") {
        return notifications.show({
            ...LOADING_NOTIFICATION_CONFIG,
            message: configuration,
            ...transformOldConfiguration(oldConfiguration),
        });
    }
    return notifications.show({
        ...LOADING_NOTIFICATION_CONFIG,
        ...configuration,
        ...transformOldConfiguration(oldConfiguration),
    });
};

/**
 *
 * @param promise Promise to be executed
 * @param data Configuration for the notification
 * @returns Notification ID
 */
const promise = <T>(promise: Promise<T>, data: PromiseData<T>): string => {
    let loadingConfig: Partial<NotificationData> = {
        ...LOADING_NOTIFICATION_CONFIG,
    };

    if (typeof data.loading === "string") {
        loadingConfig.message = data.loading;
    } else {
        loadingConfig = { ...loadingConfig, ...data.loading };
    }

    const id = notifications.show(loadingConfig as NotificationData);

    promise
        .then((result) => {
            let message: string | undefined;
            let specificConfig: Partial<NotificationData> = {};

            const successData = data.success;
            if (typeof successData === "function") {
                const res = successData(result);
                if (typeof res === "string") message = res;
                else specificConfig = res;
            } else if (typeof successData === "string") {
                message = successData;
            } else {
                specificConfig = successData;
            }

            notifications.update({
                id,
                ...SUCCESS_NOTIFICATION_CONFIG,
                ...specificConfig,
                ...(message ? { message } : {}),
            } as NotificationData);
        })
        .catch((err) => {
            let message: string | undefined;
            let specificConfig: Partial<NotificationData> = {};

            const errorData = data.error;
            if (typeof errorData === "function") {
                const res = errorData(err);
                if (typeof res === "string") message = res;
                else specificConfig = res;
            } else if (typeof errorData === "string") {
                message = errorData;
            } else {
                specificConfig = errorData;
            }

            notifications.update({
                id,
                ...ERROR_NOTIFICATION_CONFIG,
                ...specificConfig,
                ...(message ? { message } : {}),
            } as NotificationData);
        });

    return id;
};

/**
 * Updates a notification
 * @param id Notification ID
 * @param configuration Notification configuration or message
 * @param oldConfiguration Old notification configuration
 * @returns Notification ID
 */
const update = (
    id: string,
    configuration: NotificationData | string,
    oldConfiguration?: ToastOptions
): string | undefined => {
    if (typeof configuration === "string") {
        return notifications.update({
            id,
            message: configuration,
            ...transformOldConfiguration(oldConfiguration),
        });
    }
    return notifications.update({
        id,
        ...configuration,
        ...transformOldConfiguration(oldConfiguration),
    });
};

export { show, success, error, warning, loading, promise, update };
